import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GraduationCap, Clock, PlayCircle, Loader2, BookOpen } from "lucide-react";

function CourseCard({ c, progress }) {
  return (
    <Link to={`/cours/${c.id}`} data-testid={`course-card-${c.id}`} className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-lg">
      <div className="relative h-40 w-full overflow-hidden bg-slate-100">
        {c.thumbnail_url ? <img src={c.thumbnail_url} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-105" /> : <div className="flex h-full items-center justify-center"><GraduationCap className="text-slate-300" size={36} /></div>}
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold ${c.price_xof === 0 ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>{c.price_label}</span>
      </div>
      <div className="p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#0E7C7B]">{c.category}</p>
        <h3 className="mt-1 line-clamp-2 font-display font-semibold text-navy">{c.title}</h3>
        <p className="mt-1 text-xs text-slate-500">{c.instructor}</p>
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><PlayCircle size={13} /> {c.lessons_count} leçon(s)</span>
          <span className="inline-flex items-center gap-1"><Clock size={13} /> {c.total_min} min</span>
        </div>
        {progress && (
          <div className="mt-3">
            <div className="h-1.5 w-full rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-[#0E7C7B]" style={{ width: `${progress.percent}%` }} /></div>
            <p className="mt-1 text-[11px] text-slate-500">{progress.percent}% · {progress.completed_lessons}/{progress.total_lessons} leçons</p>
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Lms() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [mine, setMine] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/lms/courses${cat ? `?category=${encodeURIComponent(cat)}` : ""}`).then(({ data }) => { setCourses(data.courses); setCats(data.categories); }).finally(() => setLoading(false));
  }, [cat]);
  useEffect(() => { if (user) api.get("/lms/my-courses").then(({ data }) => setMine(data.courses)).catch(() => {}); }, [user]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10" data-testid="lms-page">
      <div className="mb-6">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0E7C7B]/10 px-3 py-1 text-xs font-semibold text-[#0E7C7B]"><GraduationCap size={13} /> Cabinet CARIS Santé — Formations</span>
        <h1 className="mt-3 font-display text-4xl font-bold text-navy sm:text-5xl">Formations médicales en ligne</h1>
        <p className="mt-2 max-w-2xl text-slate-500">Des cours à thématique médicale et de santé animés par des médecins et professionnels de santé. Apprenez à votre rythme, suivez votre progression.</p>
      </div>

      {user && (
        <div className="mb-5 flex gap-2">
          <button onClick={() => setTab("all")} data-testid="lms-tab-all" className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "all" ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}>Tous les cours</button>
          <button onClick={() => setTab("mine")} data-testid="lms-tab-mine" className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-medium ${tab === "mine" ? "bg-navy text-white" : "bg-slate-100 text-slate-600"}`}><BookOpen size={14} /> Mes cours ({mine.length})</button>
        </div>
      )}

      {tab === "all" && cats.length > 0 && (
        <div className="mb-5 flex flex-wrap gap-2">
          <button onClick={() => setCat("")} className={`rounded-full px-3 py-1 text-xs ${cat === "" ? "bg-[#0E7C7B] text-white" : "bg-slate-100 text-slate-600"}`}>Toutes</button>
          {cats.map((cc) => <button key={cc} onClick={() => setCat(cc)} className={`rounded-full px-3 py-1 text-xs ${cat === cc ? "bg-[#0E7C7B] text-white" : "bg-slate-100 text-slate-600"}`}>{cc}</button>)}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#0E7C7B]" /></div>
      ) : tab === "mine" ? (
        mine.length === 0 ? <p className="py-10 text-center text-slate-400">Vous n'êtes inscrit·e à aucun cours pour le moment.</p> :
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{mine.map((c) => <CourseCard key={c.id} c={c} progress={c.progress} />)}</div>
      ) : (
        courses.length === 0 ? <p className="py-10 text-center text-slate-400">Aucun cours disponible.</p> :
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{courses.map((c) => <CourseCard key={c.id} c={c} />)}</div>
      )}
    </div>
  );
}
